import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation, confirmAction } from '../../lib/confirm';
import {
  Card, Typography, Tag, Tabs, Select, Row, Col, Input, DatePicker, PageHeader,
  InputNumber, Space, Button, message, Modal, Table, Popconfirm, Tooltip, StatCard, EntityAvatar,
} from '../../ui';
import type { TagTone } from '../../ui';
import { useNavigate } from 'react-router-dom';
import { useUrlString, useUrlPartialRangeOrNull, useUrlStringOrNull, useUrlNumberOpt } from '../../hooks/useUrlState';
import {
  Plus, Trash2, Pencil, Download, Eye, Send, Check, X, Wallet, Clock, FileText,
  CircleCheck, Undo2, TriangleAlert, Route, Coins, MapPin, Store, HandCoins,
  Search, CalendarClock, Users, IndianRupee,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { TwDataTable as DataTable, type DataTableColumn } from '../../components/TwDataTable';
import { usePermission, useAuthStore, useHasRole } from '../../store/auth';
import { ExportDialog } from '../../components/ExportDialog';
import { FilterRange, useFilterPanel, hueOption, type FilterChip } from '../../components/FilterPanel';
import { IDENTITY_SERIES, TAG_TONE_HUE } from '../../lib/palette';
import { RoleBadge } from '../../components/RoleBadge';
import { UserLink } from '../../components/EntityLinks';
import { useDebounce } from '../../hooks/useDebounce';
import { useRetailerSearch, useFarmerSearch } from '../../hooks/useRecordSearch';
import { useServerSort } from '../../hooks/useServerSort';
import { formatIstDate } from '../../lib/time';
import { FileUploadField } from '../../components/FileUploadField';
import { stateName } from '../../constants/indianStates';
import { EditReportModal } from './EditReportModal';
import { DayExpenseModal } from './DayExpenseModal';
import { PayReimbursementDialog } from './PayReimbursementDialog';
import { ApproveExpenseReportModal } from './ApproveExpenseReportModal';
import { inr, inr2, fmtQty } from '../../lib/format';
import { displayMobile } from '../../lib/validators';

const CATEGORY_OPTIONS = [
  'TRAVEL', 'FOOD', 'LOCAL_CONVEYANCE', 'HOTEL', 'FUEL',
  'TOLL_PARKING', 'TRAIN', 'FLIGHT', 'BUS', 'OTHER',
].map((c) => ({ value: c, label: c.replace(/_/g, ' ') }));

// HOTEL and OTHER require a description (server-enforced — mirror it client-side).
const DESC_REQUIRED = new Set(['HOTEL', 'OTHER']);

const PAYMENT_MODE_OPTIONS = [
  'CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER',
].map((m) => ({ value: m, label: m.replace(/_/g, ' ') }));

const VISIT_TYPE_OPTIONS = [
  'FIELD_VISIT', 'TOUR', 'OFFICE', 'OTHER',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

const TRAVEL_MODE_OPTIONS = [
  'CAR', 'BIKE', 'TRAIN', 'FLIGHT', 'BUS', 'TAXI', 'OTHER',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

/**
 * WHERE THE MONEY STANDS — the four readings the Settlement column prints, and nothing else.
 *
 * The server holds the matching SQL for each key, so a claim cannot read "reimbursed" in the table
 * and be missing from the list this filter produces. Each option wears the colour that state already
 * wears in the column, which is what makes a CLOSED filter still say what it is set to.
 */
const SETTLEMENT_OPTIONS = [
  { value: 'TO_SETTLE', chip: 'To settle', text: 'To settle — approved, not yet paid', hue: TAG_TONE_HUE.blue },
  { value: 'REIMBURSED', chip: 'Reimbursed', text: 'Reimbursed — money paid back to the rep', hue: TAG_TONE_HUE.green },
  { value: 'FROM_ADVANCE', chip: 'Met from advance', text: 'Met from advance — nothing paid out', hue: undefined },
  { value: 'CASH_CUSTODY', chip: 'From cash custody', text: 'From cash custody — nothing to reimburse', hue: TAG_TONE_HUE.gold },
];

/**
 * A SORTABLE COLUMN'S HEADING, by the key the table reports.
 *
 * Spelt out rather than read off `columns`, which is built further down this component: the export
 * dialog has to name the order the file is in before that array exists.
 */
const SORT_HEADINGS: Record<string, string> = {
  claim: 'Claim no', day: 'Field day', subtotalAmount: 'Claimed',
  approved: 'Approved', status: 'Status', submitted: 'Filed',
};

/** Whose paperwork it is. A claim filed FOR somebody is a different thing from one they filed. */
const FILED_BY_OPTIONS = [
  { value: 'SELF', chip: 'Filed by the rep', text: 'The rep themselves' },
  { value: 'ONBEHALF', chip: 'Filed on behalf', text: 'Somebody else, on their behalf' },
];

/** One fact in the export dialog's scope block — label left, value right. */
function ScopeRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 pt-px type-label text-subtle">{label}</span>
      <span className="min-w-0 flex-1 type-body-medium text-content">{children}</span>
    </div>
  );
}

// ── Editable item row used by the modal ──
interface DraftItem {
  key: string;
  expenseDate: Dayjs | null;
  category?: string;
  description?: string;
  amount?: number;
  vendorName?: string;
  billUrl?: string;
}

const blankItem = (): DraftItem => ({
  key: Math.random().toString(36).slice(2),
  expenseDate: dayjs(),
});

/**
 * Filing your own claim: pick the FIELD DAY, then build it up in the shared `DayExpenseModal`.
 *
 * The day comes first because the claim is anchored to it — there is exactly one claim per rep per
 * day, the daily allowance is decided by that day's total distance, and the server refuses a day the
 * rep was not on duty for. Asking for the date up front makes that model visible instead of letting
 * a rep fill a form and only then discover which day it landed on.
 */
function FileMyDayClaim({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [pick, setPick] = useState<Dayjs>(dayjs());
  const [day, setDay] = useUrlStringOrNull('day');

  // That day's visits — an optional tag on a line, for a cost that belongs to one call rather than
  // to the whole day. Self-scoped by the API.
  const { data: visits } = useQuery({
    queryKey: ['my-visits-for-day', day],
    queryFn: () => request<any>({ url: '/visits', params: { date: day } }),
    enabled: !!day,
  });

  // Days that already carry a claim, so the calendar can mark them. Fetched once over the whole
  // pickable window (future dates are disabled) rather than per visible month — one small request
  // beats plumbing month-change events through the shared picker.
  const idxFrom = useMemo(() => dayjs().subtract(400, 'day').format('YYYY-MM-DD'), []);
  const idxTo = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const { data: dayIndex } = useQuery({
    queryKey: ['expense-day-index', idxFrom, idxTo],
    queryFn: () => request<any>({ url: '/expense-reports/day-index', params: { from: idxFrom, to: idxTo } }),
  });
  const filed = useMemo(() => {
    const m = new Map<string, { count: number; open: boolean }>();
    for (const r of (Array.isArray(dayIndex) ? dayIndex : [])) m.set(r.date, { count: r.count, open: r.open });
    return m;
  }, [dayIndex]);
  const pickedInfo = filed.get(pick.format('YYYY-MM-DD'));

  if (day) {
    return (
      <DayExpenseModal
        date={day}
        visits={(Array.isArray(visits) ? visits : []).map((v: any) => ({
          id: v.id,
          label: v.party?.name ? `${v.party.name}${v.visitNo ? ` · ${v.visitNo}` : ''}` : `Visit ${v.visitNo ?? v.id}`,
        }))}
        onClose={onClose}
        onSaved={() => { onSaved(); onClose(); }}
      />
    );
  }

  return (
    <Modal
      open
      title="Which field day is this for?"
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={440}
      footer={(
        <Space wrap>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => setDay(pick.format('YYYY-MM-DD'))}>Continue</Button>
        </Space>
      )}
    >
      <div className="space-y-3">
        {/* Legend sits ABOVE the field: the calendar popper opens directly beneath it and would
            cover anything placed below. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-1.5 rounded-full bg-brand-600" /> already filed
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-1.5 rounded-full bg-harvest-500" /> still open to edit
          </span>
        </div>
        <DatePicker
          value={pick}
          onChange={(v) => v && setPick(v as Dayjs)}
          disabledDate={disableFuture}
          allowClear={false}
          style={{ width: '100%' }}
          dayClassName={(d) => {
            const hit = filed.get(d.format('YYYY-MM-DD'));
            return hit ? `gb-day-marked${hit.open ? ' gb-day-marked-open' : ''}` : undefined;
          }}
        />

        {/* What picking THIS day will do. */}
        {pickedInfo && (
          <Typography.Paragraph className="text-xs" style={{ margin: 0, color: pickedInfo.open ? undefined : 'var(--gb-text-subtle)' }}>
            {pickedInfo.open
              ? `You already have a claim open for this day${pickedInfo.count > 1 ? ` (${pickedInfo.count})` : ''} — you'll carry on adding to it.`
              : `This day already has ${pickedInfo.count === 1 ? 'a submitted claim' : `${pickedInfo.count} claims`}. It can't take new expenses unless an approver sends it back.`}
          </Typography.Paragraph>
        )}

        <Typography.Paragraph type="secondary" className="text-xs" style={{ margin: 0 }}>
          Everything you spent that day — travel, food, lodging — goes on one claim. The daily
          allowance is worked out from the day's total distance when you submit. You can only claim
          for a day you were on duty.
        </Typography.Paragraph>
      </div>
    </Modal>
  );
}

interface FileExpenseReportModalProps {
  open: boolean;
  onClose: () => void;
  visitId?: string;
  onCreated?: () => void;
}

export function FileExpenseReportModal({ open, onClose, visitId, onCreated }: FileExpenseReportModalProps) {
  const navigate = useNavigate();

  // Filed FROM a visit: the visit is the source of truth for who was visited and what kind of visit it
  // was, so we read it back and freeze those fields rather than asking the filer to retype (and
  // possibly contradict) them. The claim is then linked to that visit; the rep is whoever is filing.
  const { data: visit } = useQuery({
    queryKey: ['visit-for-expense', visitId],
    queryFn: () => request<any>({ url: `/visits/${visitId}` }),
    enabled: !!open && !!visitId,
  });

  // Header fields.
  const [visitType, setVisitType] = useState<string | undefined>();
  const [partyName, setPartyName] = useState('');
  const [partyContact, setPartyContact] = useState('');
  const [city, setCity] = useState('');
  const [travelMode, setTravelMode] = useState<string | undefined>();
  const [notes, setNotes] = useState('');

  const [items, setItems] = useState<DraftItem[]>([blankItem()]);

  // Idempotency key for THIS file-report session — a retry / double-click / token-refresh
  // re-send carries the same ref, so the server returns the same report instead of a duplicate.
  // A fresh modal open mints a new ref (so a genuinely new report is created).
  const clientRef = useMemo(() => (open ? `er-${Date.now()}-${Math.random().toString(36).slice(2, 10)}` : ''), [open]);

  // Who the report is for. A manager/admin files ON BEHALF OF a sales person (when not
  // already tied to a visit); a sales person files for themselves. The party is then a
  // real retailer/farmer (scoped to that sales person), not free text.
  // Both hooks run on every render (rules of hooks); `||` would skip the second whenever the first is true.
  const approvesAsHead = usePermission('EXPENSE.APPROVE.HEAD');
  const approvesAsAccounts = usePermission('EXPENSE.APPROVE.ACCT');
  const isManager = approvesAsHead || approvesAsAccounts;
  const onBehalf = isManager && !visitId;
  const [repId, setRepId] = useState<string | undefined>();
  const [partyId, setPartyId] = useState<string | undefined>();
  const [partyAddress, setPartyAddress] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [pincode, setPincode] = useState('');

  const { data: salesPersons } = useQuery({
    queryKey: ['report-engineers'],
    queryFn: () => request<any[]>({ url: '/wallets' }),
    enabled: open && onBehalf,
  });
  // Both lists are fetched once we know whose report it is (scoped to that sales person),
  // then merged into a single party picker — no need to choose a visit type first.
  const partyReady = !onBehalf || !!repId;
  // Both halves are searched on the server with the same typed text (a first page of 200 hid the rest).
  const retailerPick = useRetailerSearch({
    key: 'report-retailers',
    params: repId ? { assignedSalesPersonId: repId } : undefined,
    enabled: open && partyReady,
    selected: partyId?.startsWith('R:') ? partyId.slice(2) : undefined,
  });
  const farmerPick = useFarmerSearch({
    key: 'report-farmers',
    params: repId ? { assignedSalesPersonId: repId } : undefined,
    enabled: open && partyReady,
    selected: partyId?.startsWith('F:') ? partyId.slice(2) : undefined,
  });
  const retailerList: any[] = retailerPick.rows;
  const farmerList: any[] = farmerPick.rows;

  // One combined party dropdown (retailers + farmers under the rep), each with a capsule.
  const partyOptions = useMemo(() => ([
    ...retailerList.map((r: any) => ({
      value: `R:${r.id}`, kind: 'RETAILER', searchText: `${r.companyName ?? ''} ${r.city ?? ''} ${r.mobile ?? ''}`,
      // The identifier beside the name, as everywhere else a customer is offered or listed: this
      // list mixes dealers and farmers, so two similar names are told apart by the number, not by
      // opening them.
      label: <Space size={6}>{r.companyName}{r.code ? <span className="type-caption text-subtle">{r.code}</span> : null}<Tag tone="blue">Retailer</Tag></Space>,
    })),
    ...farmerList.map((f: any) => ({
      value: `F:${f.id}`, kind: 'FARMER', searchText: `${f.fullName ?? ''} ${f.city ?? ''} ${f.mobile ?? ''}`,
      // A farmer has no code — the handset is what identifies them.
      label: <Space size={6}>{f.fullName}{f.mobile ? <span className="type-caption text-subtle">{displayMobile(f.mobile)}</span> : null}<Tag tone="green">Farmer</Tag></Space>,
    })),
  ]), [retailerList, farmerList]);

  // Pick a party → freeze its snapshot (name, contact, address, city, state, pincode).
  const pickParty = (value: string) => {
    setPartyId(value);
    const [k, rid] = value.split(':');
    if (k === 'R') {
      const r = retailerPick.rowFor(rid);
      if (r) { setPartyName(r.companyName ?? ''); setPartyContact(r.mobile ?? r.contactPerson ?? ''); setCity(r.city ?? ''); setStateVal(stateName(r.stateCode)); setPincode(r.pincode ?? ''); setPartyAddress([r.addressLine1, r.addressLine2].filter(Boolean).join(', ')); }
    } else {
      const f = farmerPick.rowFor(rid);
      if (f) { setPartyName(f.fullName ?? ''); setPartyContact(f.mobile ?? ''); setCity(f.city ?? ''); setStateVal(stateName(f.stateCode)); setPincode(f.pincode ?? ''); setPartyAddress(f.address ?? ''); }
    }
  };

  const reset = () => {
    setVisitType(undefined);
    setPartyName('');
    setPartyContact('');
    setCity('');
    setTravelMode(undefined);
    setNotes('');
    setRepId(undefined);
    setPartyId(undefined);
    setPartyAddress('');
    setStateVal('');
    setPincode('');
    setItems([blankItem()]);
  };

  const closeAndReset = () => { reset(); onClose(); };

  // Seed the frozen fields once the linked visit arrives (and again on every reopen).
  useEffect(() => {
    if (!open || !visit) return;
    setVisitType(visit.visitType ?? undefined);
    setPartyName(visit.party?.name ?? '');
    setPartyContact(visit.party?.mobile ?? '');
    setCity(visit.party?.location ?? '');
  }, [open, visit]);

  const patchItem = (key: string, patch: Partial<DraftItem>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, blankItem()]);
  const removeItem = (key: string) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((it) => it.key !== key) : prev));

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + Number(it.amount ?? 0), 0),
    [items],
  );

  const create = useConfirmedMutation({ title: "Create this expense claim?", okText: "Yes, create" }, {
    mutationFn: (submit: boolean) => buildAndCreate(submit),
    onSuccess: (_d, submit) => {
      message.success(submit ? 'Expense report submitted' : 'Draft saved');
      onCreated?.();
      closeAndReset();
    },
    onError: (e: any) => {
      const err = e?.response?.data?.error;
      const dupId = err?.details?.reportId;
      // One report per rep per visit — if filing from a visit that already has one, offer
      // to continue that draft instead of failing.
      if (err?.code === 'DUPLICATE_REPORT' && dupId) {
        Modal.confirm({
          title: 'A report already exists for this visit',
          content: 'Continue editing the existing report instead of starting a new one?',
          okText: 'Open existing report',
          cancelText: 'Cancel',
          onOk: () => { closeAndReset(); navigate(`/expense-reports/${dupId}`); },
        });
        return;
      }
      message.error(err?.message ?? e?.message ?? 'Could not save report');
    },
  });

  async function buildAndCreate(submit: boolean) {
    if (!visitType) throw new Error('Pick a visit type');
    if (onBehalf && !repId) throw new Error('Pick the sales person this report is for');
    const filled = items.filter((it) => it.category || it.amount != null || it.description || it.vendorName);
    if (!filled.length) throw new Error('Add at least one expense');
    for (const it of filled) {
      if (!it.category) throw new Error('Every expense needs a category');
      if (it.amount == null || Number(it.amount) <= 0) throw new Error('Every expense needs an amount');
      if (DESC_REQUIRED.has(it.category) && !it.description?.trim()) {
        throw new Error(`${it.category} expenses need a description`);
      }
    }
    const payload: any = {
      visitType,
      ...(clientRef ? { clientRef } : {}),
      ...(visitId ? { visitId } : {}),
      ...(repId ? { repId } : {}),
      ...(partyName.trim() ? { partyName: partyName.trim() } : {}),
      ...(partyContact.trim() ? { partyContact: partyContact.trim() } : {}),
      ...(partyAddress.trim() ? { partyAddress: partyAddress.trim() } : {}),
      ...(city.trim() ? { city: city.trim() } : {}),
      ...(stateVal.trim() ? { state: stateVal.trim() } : {}),
      ...(pincode.trim() ? { pincode: pincode.trim() } : {}),
      ...(travelMode ? { travelMode } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      items: filled.map((it) => ({
        category: it.category,
        amount: Number(it.amount),
        ...(it.expenseDate ? { expenseDate: it.expenseDate.format('YYYY-MM-DD') } : {}),
        ...(it.description?.trim() ? { description: it.description.trim() } : {}),
        ...(it.vendorName?.trim() ? { vendorName: it.vendorName.trim() } : {}),
        ...(it.billUrl ? { billUrl: it.billUrl } : {}),
      })),
    };
    const created = await request<any>({ url: '/expense-reports', method: 'POST', data: payload });
    if (submit && created?.id != null) {
      await request({ url: `/expense-reports/${created.id}/submit`, method: 'PATCH' });
    }
    return created;
  }

  const itemColumns = [
    {
      title: 'Date', dataIndex: 'expenseDate', width: 140,
      render: (_: any, r: DraftItem) => (
        <DatePicker
          size="small" className="w-full" value={r.expenseDate}
          onChange={(d) => patchItem(r.key, { expenseDate: d })}
        />
      ),
    },
    {
      title: 'Category', dataIndex: 'category', width: 160,
      render: (_: any, r: DraftItem) => (
        <Select
          size="small" className="w-full" placeholder="Category"
          value={r.category} onChange={(v) => patchItem(r.key, { category: v })}
          options={CATEGORY_OPTIONS}
        />
      ),
    },
    {
      title: 'Description', dataIndex: 'description', width: 200,
      render: (_: any, r: DraftItem) => (
        <Input
          size="sm"
          placeholder={r.category && DESC_REQUIRED.has(r.category) ? 'Required' : 'Optional'}
          invalid={!!(r.category && DESC_REQUIRED.has(r.category) && !r.description?.trim())}
          value={r.description} onChange={(e) => patchItem(r.key, { description: e.target.value })}
        />
      ),
    },
    {
      title: 'Amount', dataIndex: 'amount', width: 120, align: 'right' as const,
      render: (_: any, r: DraftItem) => (
        <InputNumber
          size="small" style={{ width: '100%' }} min={0} placeholder="₹"
          value={r.amount} onChange={(v) => patchItem(r.key, { amount: v == null ? undefined : Number(v) })}
        />
      ),
    },
    {
      title: 'Vendor', dataIndex: 'vendorName', width: 150,
      render: (_: any, r: DraftItem) => (
        <Input
          size="sm" placeholder="Vendor"
          value={r.vendorName} onChange={(e) => patchItem(r.key, { vendorName: e.target.value })}
        />
      ),
    },
    {
      title: 'Bill', dataIndex: 'billUrl', width: 220,
      render: (_: any, r: DraftItem) => (
        <FileUploadField
          size="small" category="receipt" preview={false} buttonLabel={r.billUrl ? 'Replace' : 'Upload'}
          value={r.billUrl} onChange={(url) => patchItem(r.key, { billUrl: url })}
        />
      ),
    },
    {
      title: '', dataIndex: 'key', width: 40,
      render: (_: any, r: DraftItem) => (
        <Button
          size="sm" variant="text" aria-label="Remove expense" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />}
          disabled={items.length <= 1} onClick={() => removeItem(r.key)}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title="File expense report"
      width={1000}
      maskClosable={false}
      onOpenChange={(o) => { if (!o) closeAndReset(); }}
      footer={[
        <Button key="cancel" onClick={closeAndReset} disabled={create.isPending}>Cancel</Button>,
        <Button
          key="draft"
          onClick={() => create.mutate(false)}
          loading={create.isPending && create.variables === false}
        >
          Save draft
        </Button>,
        <Button
          key="submit" variant="primary"
          onClick={() => create.mutate(true)}
          loading={create.isPending && create.variables === true}
        >
          Save &amp; submit
        </Button>,
      ]}
    >
      {/* Filed from a visit — say so, and show exactly what the claim will be attached to. */}
      {visitId && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-brand-500/30 bg-brand-500/5 px-3 py-2.5 text-sm">
          <span className="font-semibold text-content">Linked to visit</span>
          {visit?.visitNo && <span className="font-mono text-xs text-muted">{visit.visitNo}</span>}
          {visit?.party?.name && (
            <span className="flex items-center gap-1.5 text-content">
              {visit.party.name}
              {visit.visitType && <Tag tone={visit.visitType === 'RETAILER' ? 'blue' : visit.visitType === 'FARMER' ? 'green' : 'gold'}>{visit.visitType}</Tag>}
            </span>
          )}
          {visit?.checkInAt && <span className="text-muted">{formatIstDate(visit.checkInAt)}</span>}
          {visit?.staffName && <span className="text-muted">· visited by {visit.staffName}</span>}
          <span className="ml-auto text-xs text-muted">Claimed by you</span>
        </div>
      )}
      {onBehalf && (
        <Row gutter={[12, 10]} style={{ marginBottom: 18 }}>
          <Col xs={24} md={12}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Sales person (filing on behalf) *</Typography.Text>
            <Select
              showSearch optionFilterProp="label" className="w-full" placeholder="Select sales person"
              value={repId} onChange={(v) => { setRepId(v); setPartyId(undefined); setPartyName(''); setPartyContact(''); setCity(''); setStateVal(''); setPincode(''); setPartyAddress(''); }}
              options={(salesPersons ?? []).map((s: any) => ({ value: String(s.repId), label: s.repName ?? `#${s.repId}` }))}
            />
          </Col>
        </Row>
      )}
      <Row gutter={[12, 10]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Visit type {visitId ? '' : '*'}
          </Typography.Text>
          {/* Frozen when filed from a visit: the visit already declares its type (RETAILER / FARMER /
              PROSPECT), and letting the filer pick a different one here would contradict the link. */}
          {visitId ? (
            <Input disabled value={visitType ?? ''} placeholder="From the linked visit" />
          ) : (
            <Select
              className="w-full" placeholder="Visit type" value={visitType}
              onChange={setVisitType} options={VISIT_TYPE_OPTIONS}
            />
          )}
        </Col>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Travel mode</Typography.Text>
          <Select
            className="w-full" placeholder="Travel mode" allowClear value={travelMode}
            onChange={setTravelMode} options={TRAVEL_MODE_OPTIONS}
          />
        </Col>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Territory</Typography.Text>
          <Input disabled placeholder="From the selected party" value={city ? `${city}${stateVal ? ` · ${stateVal}` : ''}` : (stateVal || '')} />
        </Col>
      </Row>
      <Row gutter={[12, 10]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Party (retailer / farmer){!visitId && onBehalf && !repId ? ' — pick sales person first' : ''}
          </Typography.Text>
          {/* Also frozen for a visit-linked claim — the party is whoever the visit was to. */}
          {visitId ? (
            <Input disabled value={partyName} placeholder="From the linked visit" />
          ) : (
            <Select
              showSearch className="w-full" placeholder="Select retailer or farmer"
              value={partyId} onChange={pickParty} disabled={onBehalf && !repId}
              notFoundContent={onBehalf && !repId ? 'Pick a sales person first' : 'No retailers / farmers under this sales person'}
              onSearch={(v) => { retailerPick.onSearch(v); farmerPick.onSearch(v); }}
              filterOption={false}
              options={partyOptions as any}
            />
          )}
        </Col>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Party contact</Typography.Text>
          <Input placeholder="Phone / contact" value={partyContact} onChange={(e) => setPartyContact(e.target.value)} />
        </Col>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Notes</Typography.Text>
          <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Col>
      </Row>

      <Table<DraftItem>
        rowKey="key"
        size="small"
        pagination={false}
        dataSource={items}
        columns={itemColumns as any}
        scroll={{ x: 980 }}
        footer={() => (
          <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button size="sm" icon={<Plus size={16} />} onClick={addItem}>Add expense</Button>
            <Typography.Text strong>Subtotal: {inr2(subtotal)}</Typography.Text>
          </Space>
        )}
      />
    </Modal>
  );
}

interface Props {
  /** Defaults: 'all'. Approver landing → 'SUBMITTED'. Accounts landing → 'APPROVED' (to pay). */
  defaultStatus?: string;
  title?: string;
}

/** Whole days between a past moment and today (0 = today). Null when there is no date. */
const daysSince = (v: any): number | null => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? Math.max(0, dayjs().startOf('day').diff(d.startOf('day'), 'day')) : null;
};

/** "today" / "yesterday" / "12d ago" — short enough to sit under a date in a cell. */
const agoText = (v: any): string | null => {
  const d = daysSince(v);
  return d == null ? null : d === 0 ? 'today' : d === 1 ? 'yesterday' : `${d}d ago`;
};

const dash = <span className="text-subtle">—</span>;

const CHIP_TONE = {
  neutral: 'bg-surface-3 text-muted',
  brand: 'border border-brand-500/25 bg-brand-500/8 text-brand-700 dark:text-brand-300',
  sky: 'border border-sky-500/25 bg-sky-500/8 text-sky-700 dark:text-sky-400',
  amber: 'border border-warning-500/30 bg-warning-500/10 text-warning-600 dark:text-harvest-300',
  violet: 'border border-violet-500/25 bg-violet-500/8 text-violet-700 dark:text-violet-300',
} as const;

/** A small labelled fact — the unit this page is built out of (distance, allowance, a payment mode). */
function Chip({ icon, tone = 'neutral', title, children }: {
  icon?: ReactNode; tone?: keyof typeof CHIP_TONE; title?: string; children: ReactNode;
}) {
  return (
    <span title={title} className={`inline-flex max-w-full items-center gap-1 rounded-pill px-2 py-0.5 type-label ${CHIP_TONE[tone]}`}>
      {icon}<span className="truncate">{children}</span>
    </span>
  );
}

/**
 * WHAT EACH STATE MEANS, IN ONE PLACE — the words on the tag, the tab and the KPI tile all come from
 * here, so a claim cannot be called "Awaiting approval" in one control and "SUBMITTED" in the next.
 * The wording follows the tabs this page already used; only the raw enum spelling is gone.
 */
const STATUS_META: Record<string, { tone: TagTone; label: string; icon: ReactNode }> = {
  DRAFT: { tone: 'neutral', label: 'Draft', icon: <Pencil size={11} /> },
  SUBMITTED: { tone: 'gold', label: 'Awaiting approval', icon: <Clock size={11} /> },
  APPROVED: { tone: 'cyan', label: 'Approved', icon: <Check size={11} /> },
  CLOSED: { tone: 'green', label: 'Closed', icon: <CircleCheck size={11} /> },
  CORRECTION_REQUIRED: { tone: 'gold', label: 'Correction required', icon: <Undo2 size={11} /> },
  REJECTED: { tone: 'red', label: 'Rejected', icon: <X size={11} /> },
};
const statusMeta = (s: string) => STATUS_META[s] ?? { tone: 'neutral' as TagTone, label: (s ?? '').replace(/_/g, ' '), icon: null };

/** A claim paid out of collected company cash: the money is already gone, so nothing is reimbursed. */
const isCashFunded = (r: any) => r.fundingSource === 'CASH_CUSTODY';

/** The approved (payable) amount. Falls back to the claim's own subtotal on rows approved before
 *  `paid_amount` was written, which is exactly what the settlement step does server-side. */
const approvedAmount = (r: any) =>
  r.paidAmount != null ? Number(r.paidAmount) : (['APPROVED', 'CLOSED'].includes(r.status) ? Number(r.subtotalAmount ?? 0) : null);

/** How much the approver cut off the claim — the gap this page should never leave silent. */
const trimmedBy = (r: any) => {
  if (r.paidAmount == null) return 0;
  const cut = Number(r.subtotalAmount ?? 0) - Number(r.paidAmount);
  return cut > 0.005 ? cut : 0;
};

export function ExpenseReportsPage({ defaultStatus, title }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Claims are FOR field staff only (Sales Person / Sales Head). Someone else may file on a rep's
  // behalf, but never for themselves — the server refuses it (NOT_FIELD_STAFF) — so "File my report" is
  // offered only to field staff. Super Admin passes every permission check, which is how an Accounts-
  // style claim got filed in the first place.
  const canFileAny = usePermission('EXPENSE.CREATE');
  const isFieldStaff = useHasRole('SALES_PERSON', 'SALES_HEAD');
  const canApproveHead = usePermission('EXPENSE.APPROVE.HEAD');
  const canApproveAcct = usePermission('EXPENSE.APPROVE.ACCT');
  // Server gate is EXPENSE.EXPORT — deliberately NOT EXPENSE.VIEW, which every rep holds for their own
  // claims. Row scope still follows the list query, so a Head exports only their own team.
  const canExport = usePermission('EXPENSE.EXPORT');
  const [exportOpen, setExportOpen] = useState(false);
  const isManager = canApproveHead || canApproveAcct;
  // Managers file ON BEHALF (their rep picker lists field staff only); field staff file their own.
  const canCreate = canFileAny && (isManager || isFieldStaff);
  const myId = useAuthStore((s) => s.user?.id);

  const [status, setStatus] = useUrlString<string | undefined>('status', defaultStatus);
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  const [repId, setRepId] = useUrlString<string | undefined>('rep', undefined);
  const [visitType, setVisitType] = useUrlString<string | undefined>('visitType', undefined);
  // A PARTIAL range: "filed since Monday" with no end date is a filter people actually want, and the
  // shared RangePicker deliberately emits nothing until BOTH ends are picked (see the DateRange fix),
  // so a half-filled range used to do nothing at all. The server already applies each bound alone.
  const [range, setRange] = useUrlPartialRangeOrNull('range');
  /* ── THE REST OF THE REGISTER, MADE FILTERABLE ─────────────────────────────────────────────────
     Four controls (claim/party, rep, visit type, filed between) sat over a table of nine columns,
     so the questions this register is actually read for could not be asked of it: which claims are
     still owed money, whose approval a queue is waiting on, what was spent on August's field days
     as opposed to filed in August, which claims are the big ones. Every filter below narrows a
     column the table already prints, and all of them ride into the export. */
  const [fieldDay, setFieldDay] = useUrlPartialRangeOrNull('fday');
  const [minAmount, setMinAmount] = useUrlNumberOpt('minAmt');
  const [maxAmount, setMaxAmount] = useUrlNumberOpt('maxAmt');
  const [minKm, setMinKm] = useUrlNumberOpt('minKm');
  const [maxKm, setMaxKm] = useUrlNumberOpt('maxKm');
  const [settlement, setSettlement] = useUrlString<string | undefined>('settlement', undefined);
  const [paymentMode, setPaymentMode] = useUrlString<string | undefined>('paidBy', undefined);
  const [decidedBy, setDecidedBy] = useUrlString<string | undefined>('decidedBy', undefined);
  const [filedBy, setFiledBy] = useUrlString<string | undefined>('filedBy', undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState<{ id: string; action: string; label: string } | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [payId, setPayId] = useState<string | null>(null);
  const [approveId, setApproveId] = useState<string | null>(null);

  /* Server-side column sort (backend ORDER BY over the full result set, not just this page).

     THE MAP IS NOT OPTIONAL HERE. The table reports a column's own key — `claim`, `approved`,
     `submitted`, `day` — and none of those are field names the server knows. `applySort` drops a
     field it cannot resolve and quietly falls back to the default order, so four of the register's
     six sortable headings did nothing at all: the arrow moved, the rows did not, and the export
     inherited the same non-sort. */
  const { sortState, onSortChange, sort } = useServerSort({
    fieldMap: { claim: 'reportNo', day: 'expenseDate', approved: 'paidAmount', submitted: 'submittedAt' },
  });

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status) p.status = status;
    if (search.trim()) p.search = search.trim();
    if (repId) p.repId = repId;
    if (visitType) p.visitType = visitType;
    if (range?.[0]) p.from = range[0]!.format('YYYY-MM-DD');
    if (range?.[1]) p.to = range[1]!.format('YYYY-MM-DD');
    if (fieldDay?.[0]) p.dayFrom = fieldDay[0]!.format('YYYY-MM-DD');
    if (fieldDay?.[1]) p.dayTo = fieldDay[1]!.format('YYYY-MM-DD');
    if (minAmount != null) p.minAmount = minAmount;
    if (maxAmount != null) p.maxAmount = maxAmount;
    if (minKm != null) p.minKm = minKm;
    if (maxKm != null) p.maxKm = maxKm;
    if (settlement) p.settlement = settlement;
    if (paymentMode) p.paymentMode = paymentMode;
    if (decidedBy) p.decidedBy = decidedBy;
    if (filedBy) p.filedBy = filedBy;
    if (sort) p.sort = sort;
    return p;
  }, [status, search, repId, visitType, range, fieldDay, minAmount, maxAmount, minKm, maxKm,
    settlement, paymentMode, decidedBy, filedBy, sort]);
  // Counts drop the status tab, so each tab shows how many match the OTHER active filters —
  // accurate regardless of which tab is selected (the filtered list alone can't tell us this).
  const countParams = useMemo(() => { const p = { ...params }; delete p.status; return p; }, [params]);

  const { data, isLoading } = useQuery({
    queryKey: ['expense-reports', params],
    queryFn: () => request<any[]>({ url: '/expense-reports', params }),
    placeholderData: keepPreviousData,
  });
  const { data: forCounts } = useQuery({
    queryKey: ['expense-reports', 'counts', countParams],
    queryFn: () => request<any[]>({ url: '/expense-reports', params: countParams }),
    placeholderData: keepPreviousData,
  });
  const statusCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of forCounts ?? []) m[r.status] = (m[r.status] ?? 0) + 1;
    return m;
  }, [forCounts]);

  /**
   * THE MONEY BEHIND EACH QUEUE.
   *
   * Counted over `forCounts` — every active filter EXCEPT the status the tiles themselves set — so a
   * tile's figure is exactly what its click puts on screen, and each ₹ is the sum of a column the
   * table actually prints (claimed = Claimed, approved = Approved). A count alone never said whether
   * the queue was ₹900 or ₹90,000 of somebody's own money waiting to come back to them.
   */
  const kpi = useMemo(() => {
    const all = forCounts ?? [];
    const of = (s: string) => all.filter((r: any) => r.status === s);
    const sum = (rs: any[], f: (r: any) => number) => rs.reduce((a, r) => a + (f(r) || 0), 0);
    const claimed = (r: any) => Number(r.subtotalAmount ?? 0);
    const oldest = (rs: any[], f: string) => rs.reduce<number | null>((m, r) => {
      const d = daysSince(r[f]);
      return d != null && (m == null || d > m) ? d : m;
    }, null);
    const submitted = of('SUBMITTED'); const approved = of('APPROVED');
    const sentBack = of('CORRECTION_REQUIRED'); const closed = of('CLOSED');
    return {
      submitted: { n: submitted.length, amt: sum(submitted, claimed), oldest: oldest(submitted, 'submittedAt') },
      // What will actually be PAID OUT — the server's FIFO preview, not the approved total: an approved claim
      // the float covers is closed at approval, and the float can still grow under one that is waiting.
      approved: { n: approved.length, amt: sum(approved, (r) => Number(r.payableNow ?? approvedAmount(r) ?? 0)), oldest: oldest(approved, 'approvedAt') },
      sentBack: { n: sentBack.length, amt: sum(sentBack, claimed) },
      closed: {
        n: closed.length,
        amt: sum(closed, (r) => approvedAmount(r) ?? 0),
        cash: sum(closed, (r) => Number(r.reimbursementDue ?? 0)),
        advance: sum(closed, (r) => Number(r.advanceConsumed ?? 0)),
      },
      all: {
        n: all.length, amt: sum(all, claimed),
        rejected: of('REJECTED').length,
        reps: new Set(all.map((r: any) => String(r.repId))).size,
      },
    };
  }, [forCounts]);

  const loaded = !!forCounts;
  /** A tile's click REPLACES the status filter (and only that) — never stacks a second one on. */
  const pick = (s: string) => setStatus(status === s ? undefined : s);

  // Sales-person dropdown for the rep filter (managers/admins only).
  const { data: engineers } = useQuery({
    queryKey: ['report-engineers'],
    queryFn: () => request<any[]>({ url: '/wallets' }),
    enabled: isManager,
  });
  /**
   * Who can be filtered on: the wallet-holding field staff, PLUS anyone who actually appears in the
   * register. `/wallets` lists field staff only, so a claim filed by (or for) an Accounts or Admin
   * user had no option to match it — and picking that person from a link showed their raw id in the
   * box, because a Select with no matching option can only print the value it was given.
   */
  const repOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const e of engineers ?? []) byId.set(String(e.repId), e.repName ?? `#${e.repId}`);
    for (const r of forCounts ?? []) if (!byId.has(String(r.repId))) byId.set(String(r.repId), r.repName ?? `#${r.repId}`);
    return [...byId].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [engineers, forCounts]);
  /**
   * WHOSE DECISION IT WAS — built from the register's own rows, and REMEMBERED.
   *
   * `forCounts` is itself narrowed by this very filter, so a list rebuilt from scratch each time
   * would collapse to the single approver already chosen and leave no way to switch to another
   * without clearing first. Names, never ids: an approver is a person, not `#7`.
   */
  const seenDeciders = useRef(new Map<string, string>());
  const deciderOptions = useMemo(() => {
    for (const r of forCounts ?? []) {
      if (r.adminDecisionBy) {
        seenDeciders.current.set(String(r.adminDecisionBy), r.approvedByName ?? `#${r.adminDecisionBy}`);
      }
    }
    return [...seenDeciders.current].map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [forCounts]);
  const act = useMutation({
    mutationFn: ({ id, action, data }: { id: string; action: string; data?: any }) =>
      request({ url: `/expense-reports/${id}/${action}`, method: 'PATCH', data }),
    onSuccess: () => { message.success('Done'); qc.invalidateQueries({ queryKey: ['expense-reports'] }); qc.invalidateQueries({ queryKey: ['wallets'] }); qc.invalidateQueries({ queryKey: ['badges'] }); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Action failed'),
  });
  const del = useMutation({
    mutationFn: (id: string) => request({ url: `/expense-reports/${id}`, method: 'DELETE' }),
    onSuccess: () => { message.success('Draft deleted'); qc.invalidateQueries({ queryKey: ['expense-reports'] }); qc.invalidateQueries({ queryKey: ['badges'] }); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not delete'),
  });

  const rows = data ?? [];
  const filtersOn = !!(searchInput || repId || visitType || range || fieldDay || settlement
    || paymentMode || decidedBy || filedBy
    || minAmount != null || maxAmount != null || minKm != null || maxKm != null);

  /* ── WHAT IS NARROWING THE LIST, IN WORDS ──────────────────────────────────────────────────────
     One set of phrases used twice: as the removable chips in the toolbar, and as the scope block in
     the export dialog. Written once because a file and the screen that produced it must not describe
     the same filter in two different ways. */
  const dayText = (d: Dayjs | null | undefined) => (d ? d.format('DD MMM YY') : '');
  const rangeWords = (r: [Dayjs | null, Dayjs | null] | null, what: string) => (
    r?.[0] && r?.[1] ? `${what} ${dayText(r[0])} – ${dayText(r[1])}`
      : r?.[0] ? `${what} on or after ${dayText(r[0])}`
        : `${what} up to ${dayText(r?.[1])}`);
  const bandWords = (lo: number | undefined, hi: number | undefined, fmt: (n: number) => string) => (
    lo != null && hi != null ? `${fmt(lo)} – ${fmt(hi)}`
      : lo != null ? `${fmt(lo)} and above` : `up to ${fmt(hi!)}`);
  const plain = (v?: string) => String(v ?? '').replace(/_/g, ' ').toLowerCase();
  const repLabel = (id: string) => repOptions.find((o) => o.value === id)?.label ?? `#${id}`;
  const deciderLabel = (id: string) => deciderOptions.find((o) => o.value === id)?.label ?? `#${id}`;
  const settlementLabel = (v: string) => SETTLEMENT_OPTIONS.find((o) => o.value === v)?.chip ?? v;
  /** The order the register is in, in words — the sort travels with the file, so it is named. */
  const sortLabel = sortState
    ? `${SORT_HEADINGS[sortState.key] ?? sortState.key}, ${sortState.dir === 'asc' ? 'ascending' : 'descending'}`
    : 'Newest field day first';

  const clearFilters = () => {
    setSearchInput(''); setRepId(undefined); setVisitType(undefined); setRange(null);
    setFieldDay(null); setMinAmount(undefined); setMaxAmount(undefined);
    setMinKm(undefined); setMaxKm(undefined); setSettlement(undefined);
    setPaymentMode(undefined); setDecidedBy(undefined); setFiledBy(undefined);
  };
  const chips: FilterChip[] = [
    searchInput.trim() && { key: 'q', label: `Matching "${searchInput.trim()}"`, onRemove: () => setSearchInput('') },
    repId && { key: 'rep', label: `Rep: ${repLabel(repId)}`, onRemove: () => setRepId(undefined) },
    filedBy && {
      key: 'filedBy',
      label: FILED_BY_OPTIONS.find((o) => o.value === filedBy)?.chip ?? filedBy,
      onRemove: () => setFiledBy(undefined),
    },
    decidedBy && { key: 'decidedBy', label: `Decided by ${deciderLabel(decidedBy)}`, onRemove: () => setDecidedBy(undefined) },
    fieldDay && { key: 'fday', label: rangeWords(fieldDay, 'Field day'), onRemove: () => setFieldDay(null) },
    range && { key: 'range', label: rangeWords(range, 'Filed'), onRemove: () => setRange(null) },
    visitType && { key: 'visitType', label: `Visit: ${plain(visitType)}`, onRemove: () => setVisitType(undefined) },
    (minKm != null || maxKm != null) && {
      key: 'km',
      label: bandWords(minKm, maxKm, (n) => `${fmtQty(n)} km`),
      onRemove: () => { setMinKm(undefined); setMaxKm(undefined); },
    },
    (minAmount != null || maxAmount != null) && {
      key: 'amt',
      label: `Claimed ${bandWords(minAmount, maxAmount, (n) => inr(n))}`,
      onRemove: () => { setMinAmount(undefined); setMaxAmount(undefined); },
    },
    settlement && { key: 'settlement', label: settlementLabel(settlement), onRemove: () => setSettlement(undefined) },
    paymentMode && { key: 'paidBy', label: `Paid by ${plain(paymentMode)}`, onRemove: () => setPaymentMode(undefined) },
  ].filter(Boolean) as FilterChip[];

  /* Eleven controls do not fit on a toolbar — as a bare row they wrap onto three lines, take a
     different width each, and give a min/max pair that is really ONE filter the weight of two. They
     open IN PLACE under the toolbar instead, in sections that follow the columns they narrow, and
     what is applied stays visible as chips whether the panel is open or shut.

     Status is deliberately not here: it is the tab strip and the tile band above, and a second
     control for it would be a second answer to one question. */
  const filters = useFilterPanel({
    chips,
    onClear: clearFilters,
    groups: [
      {
        title: 'Whose claim',
        tone: IDENTITY_SERIES[1],
        icon: <Users size={12} />,
        fields: [
          ...(isManager ? [{
            label: 'Sales person',
            control: (
              <Select placeholder="Anyone" allowClear showSearch optionFilterProp="label"
                value={repId} onChange={(v) => setRepId((v as string) || undefined)} options={repOptions} />
            ),
          }] : []),
          {
            label: 'Filed by',
            hint: 'a claim filed FOR a rep is not the same as one they filed',
            control: (
              <Select placeholder="Anyone" allowClear value={filedBy}
                onChange={(v) => setFiledBy((v as string) || undefined)}
                options={FILED_BY_OPTIONS.map((o) => ({ value: o.value, label: o.text }))} />
            ),
          },
          ...(isManager ? [{
            label: 'Decided by',
            hint: 'the approver who passed or refused it',
            control: (
              <Select placeholder="Anyone" allowClear showSearch optionFilterProp="label"
                value={decidedBy} onChange={(v) => setDecidedBy((v as string) || undefined)}
                options={deciderOptions} />
            ),
          }] : []),
        ],
      },
      {
        title: 'When',
        tone: IDENTITY_SERIES[2],
        icon: <CalendarClock size={12} />,
        fields: [
          {
            /* THE FIELD DAY, which is what a claim IS — and never the same question as when the
               paperwork appeared. A claim for the 3rd filed on the 28th belongs to August's field
               work and September's filing at once. Either end alone is a whole question: the server
               applies each bound on its own. */
            label: 'Field day',
            span: 2,
            control: (
              <FilterRange
                from={(
                  <DatePicker placeholder="From" aria-label="Field day from" disabledDate={disableFuture}
                    value={fieldDay?.[0] ?? null}
                    onChange={(d) => setFieldDay((prev) => { const to = prev?.[1] ?? null; return d || to ? [d, to] : null; })} />
                )}
                to={(
                  <DatePicker placeholder="To" aria-label="Field day to" disabledDate={disableFuture}
                    value={fieldDay?.[1] ?? null}
                    onChange={(d) => setFieldDay((prev) => { const from = prev?.[0] ?? null; return d || from ? [from, d] : null; })} />
                )}
              />
            ),
          },
          {
            label: 'Filed',
            span: 2,
            hint: 'when the paperwork appeared',
            control: (
              <FilterRange
                from={(
                  <DatePicker placeholder="From" aria-label="Filed from" disabledDate={disableFuture}
                    value={range?.[0] ?? null}
                    onChange={(d) => setRange((prev) => { const to = prev?.[1] ?? null; return d || to ? [d, to] : null; })} />
                )}
                to={(
                  <DatePicker placeholder="To" aria-label="Filed to" disabledDate={disableFuture}
                    value={range?.[1] ?? null}
                    onChange={(d) => setRange((prev) => { const from = prev?.[0] ?? null; return d || from ? [from, d] : null; })} />
                )}
              />
            ),
          },
        ],
      },
      {
        title: 'The day itself',
        tone: IDENTITY_SERIES[5],
        icon: <MapPin size={12} />,
        fields: [
          {
            label: 'Visit type',
            control: (
              <Select placeholder="Any type" allowClear value={visitType}
                onChange={(v) => setVisitType((v as string) || undefined)} options={VISIT_TYPE_OPTIONS} />
            ),
          },
          {
            label: 'Distance (km)',
            span: 2,
            hint: 'the day’s total — what the daily allowance is worked out from',
            control: (
              <FilterRange
                from={(
                  <InputNumber min={0} placeholder="e.g. 80" aria-label="Minimum distance"
                    value={minKm ?? null} onChange={(v) => setMinKm(v == null ? undefined : Number(v))} />
                )}
                to={(
                  <InputNumber min={0} placeholder="e.g. 200" aria-label="Maximum distance"
                    value={maxKm ?? null} onChange={(v) => setMaxKm(v == null ? undefined : Number(v))} />
                )}
              />
            ),
          },
        ],
      },
      {
        title: 'How much',
        tone: IDENTITY_SERIES[0],
        icon: <IndianRupee size={12} />,
        fields: [{
          label: 'Claimed',
          span: 2,
          hint: 'either end on its own is a whole question',
          control: (
            <FilterRange
              from={(
                <InputNumber min={0} placeholder="e.g. 500" aria-label="Minimum claimed"
                  value={minAmount ?? null} onChange={(v) => setMinAmount(v == null ? undefined : Number(v))} />
              )}
              to={(
                <InputNumber min={0} placeholder="e.g. 5000" aria-label="Maximum claimed"
                  value={maxAmount ?? null} onChange={(v) => setMaxAmount(v == null ? undefined : Number(v))} />
              )}
            />
          ),
        }],
      },
      {
        title: 'Settlement',
        tone: IDENTITY_SERIES[3],
        icon: <Wallet size={12} />,
        fields: [
          {
            /* The same four readings the Settlement column prints, wearing the same colours — so the
               filter and the cell it narrows can never say different things about one claim. */
            label: 'Where the money stands',
            span: 2,
            control: (
              <Select placeholder="Any" allowClear value={settlement}
                onChange={(v) => setSettlement((v as string) || undefined)}
                options={SETTLEMENT_OPTIONS.map((o) => hueOption(o.value, o.text, o.hue))} />
            ),
          },
          {
            label: 'Paid by',
            hint: 'how a reimbursement actually left the company',
            control: (
              <Select placeholder="Any mode" allowClear value={paymentMode}
                onChange={(v) => setPaymentMode((v as string) || undefined)} options={PAYMENT_MODE_OPTIONS} />
            ),
          },
        ],
      },
    ],
  });

  const columns: DataTableColumn<any>[] = [
    {
      // The claim AND whose money it is. They were two columns of flat text; together they are the
      // row's identity, and the rep's own colour (steady across every page that draws them) is what
      // finally lets the eye tell one row from the next.
      key: 'claim', title: 'Claim', dataIndex: 'reportNo', sortable: true, alwaysVisible: true, width: 232,
      render: (v: any, r: any) => {
        const rep = r.repName ?? `#${r.repId}`;
        const onBehalf = String(r.createdBy) !== String(r.repId);
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            {/* Their actual face when the profile carries one; initials on their own steady hue otherwise. */}
            <EntityAvatar name={rep} src={r.repPhoto} size={34} />
            <div className="min-w-0">
              <div className="truncate type-body-strong tabular-nums">{v}</div>
              <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1">
                <UserLink id={r.repId} role={r.repRole} className="truncate type-caption">{rep}</UserLink>
                {/* The REP's own role — not the submitter's, which is a different person's role
                    entirely on a claim filed on their behalf. */}
                <RoleBadge role={r.repRole} />
                {String(r.repId) === String(myId) && <Tag tone="green">you</Tag>}
              </div>
              {onBehalf && (
                <div className="mt-0.5 flex flex-wrap items-center gap-1 type-caption text-muted">
                  filed by {r.createdByName ?? 'someone else'}
                  <RoleBadge role={r.createdByRoleCode} />
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      // THE FIELD DAY — the thing a claim actually is. There is one claim per rep per day, the
      // allowance is decided by that day's distance, and until now the register never showed the day
      // at all: it showed when the paperwork was filed, which for a claim filed weeks later is a
      // different date entirely.
      key: 'day', title: 'Field day', dataIndex: 'expenseDate', sortable: true, width: 210,
      render: (v: any, r: any) => {
        const km = Number(r.totalDistanceKm ?? 0);
        const bits: ReactNode[] = [];
        if (km > 0) bits.push(<Chip key="km" tone="sky" icon={<Route size={11} />}>{fmtQty(km)} km</Chip>);
        if (r.visitType) bits.push(<Chip key="vt" icon={<MapPin size={11} />}>{String(r.visitType).replace(/_/g, ' ')}</Chip>);
        if (r.partyName) bits.push(<Chip key="p" tone="violet" icon={<Store size={11} />}>{r.partyName}</Chip>);
        return (
          <div className="min-w-0">
            {v ? (
              <>
                <div className="type-body-medium">{formatIstDate(v)}</div>
                <div className="type-caption text-muted">{dayjs(v).format('dddd')} · {agoText(v)}</div>
              </>
            ) : (
              <Chip tone="amber" icon={<TriangleAlert size={11} />} title="Filed before claims were anchored to a field day">no day on record</Chip>
            )}
            {!!bits.length && <div className="mt-1 flex flex-wrap items-center gap-1">{bits}</div>}
          </div>
        );
      },
    },
    {
      key: 'subtotalAmount', title: 'Claimed', dataIndex: 'subtotalAmount', sortable: true,
      align: 'right', alwaysVisible: true, width: 128,
      render: (v: any, r: any) => (
        <div>
          <div className="type-body-strong tabular-nums">{inr2(v)}</div>
          {/* The daily allowance is EARNED by the day's distance, not spent — saying so here stops it
              reading as one more bill the rep is claiming for. */}
          {Number(r.daAmount ?? 0) > 0 && (
            <div className="type-caption tabular-nums text-muted" title="Daily allowance for the day's distance">
              <Coins size={11} className="mr-1 inline align-[-1px] text-brand-600 dark:text-brand-400" />
              incl. DA {inr2(r.daAmount)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'approved', title: 'Approved', dataIndex: 'paidAmount', sortable: true, align: 'right', width: 138,
      render: (_: any, r: any) => {
        const amt = approvedAmount(r);
        if (amt == null) return dash;
        const cut = trimmedBy(r);
        return (
          <div>
            <div className="type-body-strong tabular-nums text-brand-700 dark:text-brand-300">{inr2(amt)}</div>
            {cut > 0 && (
              <div className="type-caption tabular-nums text-warning-600 dark:text-harvest-300" title="The approver reduced this claim">
                −{inr2(cut)} trimmed
              </div>
            )}
          </div>
        );
      },
    },
    {
      // WHERE THE MONEY CAME FROM — the half of this workflow the register never showed. A claim is
      // met advance-first: the rep's float covers what it can, and only the shortfall is actually
      // paid out. "Closed" alone could mean ₹0 changed hands or ₹5,000 did.
      key: 'settlement', title: 'Settlement', width: 196,
      render: (_: any, r: any) => {
        /* MONEY OWED BACK — a reimbursement already paid on this claim before it was reopened or sent back. It is the
           fact that needs acting on, so it leads the cell. */
        const owedBack = Number(r.recoveryOutstanding ?? 0);
        if (owedBack > 0) {
          return (
            <div className="min-w-0 space-y-0.5">
              <div className="type-body-medium tabular-nums text-warning-600 dark:text-harvest-300">{inr2(owedBack)} owed back</div>
              <div className="type-caption text-muted">reimbursed before the claim was reversed</div>
            </div>
          );
        }
        /* SETTLEMENT V2 — the split frozen at approval: wallet, collection, and what is (or was) reimbursed. */
        if (r.settlementVersion === 'V2' && r.settlementSplit) {
          const sp = r.settlementSplit;
          const owed = Number(r.reimbursementOutstanding ?? 0);
          const due = Number(sp.reimbursementDue ?? 0);
          return (
            <div className="min-w-0 space-y-0.5">
              <div className={`type-body-medium tabular-nums ${r.status === 'APPROVED' ? 'text-sky-700 dark:text-sky-400' : due > 0 ? 'text-brand-700 dark:text-brand-300' : 'text-muted'}`}>
                {r.status === 'APPROVED' ? `${inr2(owed)} to reimburse` : due > 0 ? `${inr2(due)} reimbursed` : 'nothing to reimburse'}
              </div>
              <div className="type-caption text-muted tabular-nums">wallet {inr2(sp.walletUsed)} · collection {inr2(sp.collectionUsed)}</div>
            </div>
          );
        }
        if (isCashFunded(r)) {
          return (
            <div className="min-w-0">
              <Chip tone="amber" icon={<HandCoins size={11} />}>from cash custody</Chip>
              <div className="mt-0.5 type-caption text-muted">nothing to reimburse</div>
            </div>
          );
        }
        if (r.status === 'APPROVED') {
          /* The server's FIFO preview (`payableNow` / `floatCovers`) — the same split the claim page and
             the pay dialog show. Printing the approved total here called ₹698 "to settle" on a claim
             that would pay out ₹0. */
          const pay = r.payableNow != null ? Number(r.payableNow) : (approvedAmount(r) ?? 0);
          const covered = Number(r.floatCovers ?? 0);
          return (
            <div className="min-w-0">
              <div className="type-body-medium tabular-nums text-sky-700 dark:text-sky-400">
                {pay > 0 ? `${inr2(pay)} to pay` : 'nothing to pay'}
              </div>
              <div className="type-caption text-muted tabular-nums">
                {covered > 0 ? `${inr2(covered)} met from advance` : 'no advance held'}
              </div>
            </div>
          );
        }
        if (r.status === 'CLOSED') {
          const adv = Number(r.advanceConsumed ?? 0);
          const paid = Number(r.reimbursementDue ?? 0);
          if (r.reimbursementDue == null && r.advanceConsumed == null) {
            return (
              <div className="min-w-0">
                <div className="type-body-medium text-muted">settled</div>
                <div className="type-caption text-subtle">before the split was recorded</div>
              </div>
            );
          }
          return (
            <div className="min-w-0 space-y-0.5">
              {paid > 0
                ? <div className="type-body-medium tabular-nums text-brand-700 dark:text-brand-300">{inr2(paid)} reimbursed</div>
                : <div className="type-body-medium text-muted">nothing paid out</div>}
              <div className="type-caption text-muted tabular-nums">
                {adv > 0 ? `${inr2(adv)} met from advance` : 'no advance held'}
              </div>
            </div>
          );
        }
        if (r.status === 'REJECTED') return <span className="type-caption text-muted">nothing payable</span>;
        return dash;
      },
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true, alwaysVisible: true, width: 178,
      render: (s: string, r: any) => {
        const m = statusMeta(s);
        // The second line is the AGE of the state the row is actually in — a claim waiting five weeks
        // for an approver looks identical to one filed this morning without it.
        let note: ReactNode = null;
        if (s === 'SUBMITTED') {
          const d = daysSince(r.submittedAt);
          note = d == null ? null : (
            <span className={d > 7 ? 'text-warning-600 dark:text-harvest-300' : 'text-muted'}>
              {d > 7 && <TriangleAlert size={10} className="mr-1 inline align-[-1px]" />}
              waiting {d}d
            </span>
          );
        } else if (s === 'APPROVED') {
          const d = daysSince(r.approvedAt);
          note = <span className={(d ?? 0) > 7 ? 'text-warning-600 dark:text-harvest-300' : 'text-muted'}>unpaid {d ?? 0}d</span>;
        } else if (s === 'CLOSED' && r.paidAt) {
          // Only a claim that paid money out was "paid"; one the float or collected cash met was settled.
          note = <span className="text-muted">{Number(r.reimbursementDue ?? 0) > 0 ? 'paid' : 'settled'} {formatIstDate(r.paidAt)}</span>;
        } else if (s === 'CORRECTION_REQUIRED') {
          note = <span className="text-muted">back with the rep</span>;
        }
        return (
          <div className="min-w-0">
            <Tag tone={m.tone} icon={m.icon}>{m.label}</Tag>
            {note && <div className="mt-0.5 type-caption">{note}</div>}
          </div>
        );
      },
    },
    {
      key: 'submitted', title: 'Filed', dataIndex: 'submittedAt', sortable: true, width: 170,
      render: (v: string, r: any) => (
        <div className="min-w-0">
          <div className="type-body-medium">{formatIstDate(v ?? r.createdAt)}</div>
          {r.submittedByName && (
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1">
              <span className="truncate type-caption text-muted">{r.submittedByName}</span>
              {r.submittedByRole && <RoleBadge role={r.submittedByRole} />}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'decision', title: 'Decided by', dataIndex: 'approvedByName', width: 190,
      render: (v: any, r: any) => {
        if (!v) return r.status === 'SUBMITTED' ? <span className="type-caption text-muted">nobody yet</span> : dash;
        const rejected = r.status === 'REJECTED';
        return (
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              <span className={`truncate ${rejected ? 'text-danger-600 dark:text-danger-500' : 'text-brand-700 dark:text-brand-300'}`}>{v}</span>
              {r.approvedByRole && <RoleBadge role={r.approvedByRole} />}
            </div>
            <div className="type-caption text-muted">
              {rejected ? 'rejected' : 'approved'} {r.approvedAt ? formatIstDate(r.approvedAt) : ''}
            </div>
          </div>
        );
      },
    },
    {
      // Icon-only row actions (32px squares; the wording lives in the tooltip + aria-label), laid out
      // in a FIXED order — pay · submit · approve · edit · reject · delete · open — so the same
      // decision is always in the same place whatever state a report is in. Six labelled buttons
      // needed a 300px column and still wrapped onto two lines.
      // WIDE ENOUGH FOR THE BUSIEST ROW. A draft carries four icons (submit · edit · delete · open) =
      // 140px of buttons, and under `layoutFixed` every column is scaled down proportionally, so a
      // 152px request became ~135px on a laptop and the fourth icon dropped onto a second line — the
      // row actions read as two stacked columns. Sized for the maximum, and told never to wrap.
      key: 'actions', title: 'Action', dataIndex: 'id', alwaysVisible: true, width: 200, align: 'right',
      render: (_: any, r: any) => {
        const id = String(r.id);
        const mine = String(r.repId) === String(myId);
        // DEF-060 · whoever FILED a claim on a rep's behalf may not decide it: the server refuses their verify,
        // approve, reject and send back (FILED_ON_BEHALF) and another approver in scope decides it. Same test as
        // the server's blockFiler, so the filer is not offered "Review & approve" / "Reject report" on that row.
        const filedForRep = r.createdBy != null && String(r.createdBy) === String(myId) && String(r.createdBy) !== String(r.repId);
        // Editable only by the beneficiary (owner) or the filer (creator), while draft / sent back.
        const canEditDraft = ['DRAFT', 'CORRECTION_REQUIRED'].includes(r.status) && (mine || String(r.createdBy) === String(myId));
        const stop = (e: any) => e.stopPropagation();
        const btns: any[] = [];
        // Approved but not yet paid — Accounts settles it right here, in the same dialog the claim page
        // opens. Never on your own claim: the server refuses a self-settlement (SELF_ACTION).
        if (r.status === 'APPROVED' && canApproveAcct && !mine) {
          btns.push(
            <Tooltip key="pay" label="Pay this report">
              <Button size="sm" variant="primary" aria-label="Pay this report" icon={<Wallet size={16} />}
                onClick={(e) => { stop(e); setPayId(id); }} />
            </Tooltip>,
          );
        }
        if (canEditDraft) {
          btns.push(
            <Tooltip key="su" label="Submit for approval">
              <Popconfirm title="Submit this report for approval?" okText="Submit"
                onConfirm={() => act.mutate({ id, action: 'submit' })}>
                <Button size="sm" variant="primary" aria-label="Submit for approval" icon={<Send size={16} />} onClick={stop} />
              </Popconfirm>
            </Tooltip>,
          );
        }
        // Single approval layer — any approver approves / rejects a submitted report. Approving opens
        // the claim for review first (expenses, receipts, verification, amount) — never a blind
        // one-click yes. Not on your own claim: the server refuses self-approval (SELF_ACTION).
        if (r.status === 'SUBMITTED' && isManager && !mine && !filedForRep) {
          btns.push(
            <Tooltip key="ap" label="Review & approve">
              <Button size="sm" variant="primary" aria-label="Review & approve" icon={<Check size={16} />}
                onClick={(e) => { stop(e); setApproveId(id); }} />
            </Tooltip>,
          );
        }
        if (canEditDraft) {
          btns.push(
            <Tooltip key="edit" label="Edit report">
              <Button size="sm" variant="ghost" aria-label="Edit report" icon={<Pencil size={16} />}
                onClick={(e) => { stop(e); setEditId(id); }} />
            </Tooltip>,
          );
        }
        if (r.status === 'SUBMITTED' && isManager && !mine && !filedForRep) {
          btns.push(
            <Tooltip key="rj" label="Reject report">
              <Button size="sm" variant="soft-danger" aria-label="Reject report" icon={<X size={16} />}
                onClick={(e) => { stop(e); setRejectFor({ id, action: 'reject', label: 'Reject report' }); }} />
            </Tooltip>,
          );
        }
        // Discard an unwanted draft (owner or the manager who filed it).
        if (canEditDraft) {
          btns.push(
            <Tooltip key="del" label="Delete draft">
              <Popconfirm title="Delete this draft? This can't be undone." okText="Delete" okButtonProps={{ danger: true }}
                onConfirm={() => del.mutate(id)}>
                <Button size="sm" variant="text" aria-label="Delete draft"
                  icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} onClick={stop} />
              </Popconfirm>
            </Tooltip>,
          );
        }
        // Open is on every row, so it holds the last slot on every row.
        btns.push(
          <Tooltip key="o" label="Open the report">
            <Button size="sm" variant="text" aria-label="Open the report" icon={<Eye size={16} />}
              onClick={(e) => { stop(e); navigate(`/expense-reports/${id}`); }} />
          </Tooltip>,
        );
        // RIGHT-aligned: rows carry different numbers of actions (a draft has five, a closed report
        // has one), so a left-aligned cluster put the eye icon at a different x on every row. Anchored
        // to the right edge, the always-present Open icon lines up down the column and the optional
        // ones grow leftwards from it.
        return <div className="flex flex-nowrap items-center justify-end gap-1" onClick={stop}>{btns}</div>;
      },
    },
  ];

  const tabItems = [
    { key: 'all', label: tabLabel('All', forCounts?.length) },
    { key: 'DRAFT', label: tabLabel('Drafts', statusCounts.DRAFT) },
    { key: 'SUBMITTED', label: tabLabel('Awaiting approval', statusCounts.SUBMITTED, 'gold') },
    { key: 'APPROVED', label: tabLabel('Approved', statusCounts.APPROVED, 'cyan') },
    { key: 'CLOSED', label: tabLabel('Closed', statusCounts.CLOSED, 'green') },
    { key: 'CORRECTION_REQUIRED', label: tabLabel('Correction required', statusCounts.CORRECTION_REQUIRED, 'gold') },
    { key: 'REJECTED', label: tabLabel('Rejected', statusCounts.REJECTED, 'red') },
  ];

  return (
    <div>
      <PageHeader
        icon={<Wallet />}
        title={title ?? 'Expense Reports'}
        subtitle="One claim per rep per field day — what was spent, what the approver allowed, and what is still to be paid back."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canExport && (
              <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
            )}
            {canCreate && (
              <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setModalOpen(true)}>
                {/* An approver's click opens the on-behalf form (pick the rep first); a rep files their own. */}
                {isManager ? 'File on behalf' : 'File my report'}
              </Button>
            )}
          </div>
        }
      />

      {/* THE QUEUES, LED BY THEIR MONEY. Each tile selects the tab that holds it (clicking the active
          one goes back to All), and its ₹ is the sum of a column the table prints for exactly those
          rows — so a tile can be checked by opening it and adding up. A tile with nothing in it is
          not offered as a filter. */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <StatCard
          tone="gold" icon={<Clock size={18} />} label="Awaiting approval"
          value={loaded ? inr(kpi.submitted.amt) : '—'}
          hint={kpi.submitted.n
            ? `${kpi.submitted.n} ${kpi.submitted.n === 1 ? 'claim' : 'claims'}${kpi.submitted.oldest != null ? ` · oldest ${kpi.submitted.oldest}d` : ''}`
            : 'nothing waiting on an approver'}
          onClick={kpi.submitted.n ? () => pick('SUBMITTED') : undefined} active={status === 'SUBMITTED'}
        />
        <StatCard
          tone="sky" icon={<Wallet size={18} />} label="Approved, not yet paid"
          value={loaded ? inr(kpi.approved.amt) : '—'}
          hint={kpi.approved.n
            ? `${kpi.approved.n} ${kpi.approved.n === 1 ? 'claim waits' : 'claims wait'} for a payment${kpi.approved.oldest != null ? ` · oldest ${kpi.approved.oldest}d` : ''}`
            : 'every approved claim is settled'}
          onClick={kpi.approved.n ? () => pick('APPROVED') : undefined} active={status === 'APPROVED'}
        />
        <StatCard
          tone="warning" icon={<Undo2 size={18} />} label="Sent back"
          value={loaded ? inr(kpi.sentBack.amt) : '—'}
          hint={kpi.sentBack.n
            ? `${kpi.sentBack.n} with the rep to fix and resubmit`
            : 'nothing waiting on a rep'}
          onClick={kpi.sentBack.n ? () => pick('CORRECTION_REQUIRED') : undefined} active={status === 'CORRECTION_REQUIRED'}
        />
        <StatCard
          tone="brand" icon={<CircleCheck size={18} />} label="Settled"
          value={loaded ? inr(kpi.closed.amt) : '—'}
          hint={kpi.closed.n
            ? `${kpi.closed.n} closed · ${inr(kpi.closed.cash)} paid out, rest from advances`
            : 'nothing settled in this period'}
          onClick={kpi.closed.n ? () => pick('CLOSED') : undefined} active={status === 'CLOSED'}
        />
        <StatCard
          tone="neutral" icon={<FileText size={18} />} label="Claimed in all"
          value={loaded ? inr(kpi.all.amt) : '—'}
          hint={loaded
            ? `${kpi.all.n} claims · ${kpi.all.reps} ${kpi.all.reps === 1 ? 'rep' : 'reps'}${kpi.all.rejected ? ` · incl. ${kpi.all.rejected} rejected` : ''}`
            : 'loading'}
          onClick={status ? () => setStatus(undefined) : undefined} active={!status}
        />
      </div>

      {/* The card's own padding stays off so the tab strip can draw its rule edge to edge; every
          block inside it is inset instead. Without that the filter row, the column headings and the
          pager all ran flush into the card's hairline. */}
      <Card bodyClassName="p-0 sm:p-0">
        <div className="px-4 pt-2 sm:px-5">
          <Tabs activeKey={status ?? 'all'} onChange={(k) => setStatus(k === 'all' ? undefined : k)} items={tabItems} />
        </div>
        <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
        <DataTable
          storageKey="expense-reports-v2"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          bordered={false}
          layoutFixed
          emptyText={filtersOn ? 'No claim matches these filters — clear one to widen the list' : 'No expense reports found'}
          onRow={(r: any) => ({
            onClick: () => navigate(`/expense-reports/${r.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            /* The claim number is what this register is typed into, so it stays on the toolbar where
               it can be reached without opening anything. Everything else lives in the panel — see
               `filters` above for why — with what is applied always visible beside it. */
            <>
              <Input
                prefix={<Search size={16} className="text-subtle" />}
                placeholder="Report no or party" style={{ width: 220 }} allowClear
                aria-label="Search claims by number or party"
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              />
              {filters.trigger}
              {filters.chips}
            </>
          }
          toolbarBelow={filters.panel}
        />
        </div>
      </Card>

      {/* A rep files against a FIELD DAY, not a visit — same model and same modal as My Day, so both
          entry points produce identical claims. A manager filing ON BEHALF still uses the older form
          (it needs a rep picker, which the day modal does not carry yet). */}
      {modalOpen && (isManager ? (
        <FileExpenseReportModal
          open
          onClose={() => setModalOpen(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['expense-reports'] })}
        />
      ) : (
        <FileMyDayClaim
          onClose={() => setModalOpen(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['expense-reports'] })}
        />
      ))}
      {rejectFor && (
        <ReasonModal title={rejectFor.label} onClose={() => setRejectFor(null)}
          onSubmit={(reason) => confirmAction(
            { title: `${rejectFor.label}?`, content: 'Your reason goes back to whoever filed the claim.', okText: 'Yes, continue', danger: true },
            () => { act.mutate({ id: rejectFor.id, action: rejectFor.action, data: { reason } }); setRejectFor(null); },
          )} />
      )}
      {payId && <PayReimbursementDialog reportId={payId} onClose={() => setPayId(null)} />}
      {approveId && <ApproveExpenseReportModal reportId={approveId} onClose={() => setApproveId(null)} />}
      {editId && (
        <EditReportModal
          reportId={editId} onClose={() => setEditId(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['expense-reports'] }); qc.invalidateQueries({ queryKey: ['expense-report', editId] }); }}
        />
      )}
      {/* NO PERIOD OF ITS OWN, AND NO FILTERS OF ITS OWN.
          The dialog used to carry a second date range over a page that already has two, so the file
          could disagree with the screen that produced it and nothing in either said which range had
          won. It takes THIS page's live filters now — every one of them, plus the column sort — and
          prints them back instead of asking for them again. Change the filters behind the dialog to
          change the file. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export expense claims"
        description="Exactly what this page is showing: what was claimed, the daily allowance, distance, how it was settled and by whom."
        // Three shapes of the same rows: a PDF to read or file, an Excel workbook with totals, and
        // plain CSV for someone loading the register into their own model. CSV is listed only because
        // the server now actually serves `export.csv` — an offered format that 404s is worse than one
        // that is absent.
        formats={['pdf', 'xlsx', 'csv']}
        baseUrl="/expense-reports/export"
        /* `params` WHOLE: the filed range, the field-day range, the amount and distance bands, the
           settlement state and `sort` all ride along. The server rebuilds the very same list. */
        baseParams={params}
        hidePeriod
        scopeSummary={(
          <div className="space-y-1">
            <ScopeRow label="Claims">
              {status ? statusMeta(status).label : 'Every status'}
              {' · '}
              {rows.length.toLocaleString('en-IN')} row{rows.length === 1 ? '' : 's'} — what the list below is showing
            </ScopeRow>
            <ScopeRow label="Field day">{fieldDay ? rangeWords(fieldDay, 'Field day').replace('Field day ', '') : 'All time'}</ScopeRow>
            <ScopeRow label="Filed">{range ? rangeWords(range, 'Filed').replace('Filed ', '') : 'All time'}</ScopeRow>
            <ScopeRow label="Staff">{repId ? repLabel(repId) : 'Everyone in your scope'}</ScopeRow>
            {/* "Filters: None" over a narrowed file is the one thing this block must never say. */}
            <ScopeRow label="Filters">
              {chips.filter((c) => !['fday', 'range', 'rep'].includes(c.key)).length
                ? chips.filter((c) => !['fday', 'range', 'rep'].includes(c.key)).map((c) => c.label).join(' · ')
                : 'None'}
            </ScopeRow>
            <ScopeRow label="Order">{sortLabel}</ScopeRow>
          </div>
        )}
      />
    </div>
  );
}

/** A tab title with its count as a tone-matched chip (the count is dropped when there is nothing). */
function tabLabel(text: string, count?: number, tone?: TagTone) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {text}
      {count ? <Tag tone={tone} style={{ marginInlineEnd: 0 }}>{count}</Tag> : null}
    </span>
  );
}

// Reason capture for a manager/admin reject (reason is required by the backend).
function ReasonModal({ title, onClose, onSubmit }: { title: string; onClose: () => void; onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={title}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={!reason.trim()} onClick={() => onSubmit(reason.trim())}>Confirm</Button>
      </>}>
      <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (visible to the sales person)" />
    </Modal>
  );
}
